import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiSearch, FiPlus, FiInfo } from 'react-icons/fi';
import { FaYoutube, FaDiscord, FaTwitch, FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa';
import { BsPatchCheckFill } from 'react-icons/bs';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import logo from '../../../../assets/images/logo.png';
import { getContentCreators } from '../../../../store/contentCreators/contentCreatorsSlice';
import {
  PageWrapper, PageBanner, ControlsBar, FilterBtn,
  CreatorsGrid, CreatorCard,
} from './ContentCreatorsStyle';
import { backendUrl } from "../../../../utilities/axios";
import { SkeletonCreatorCard, NoData } from '../../../shared/WebsiteFeedback';

const PLT_META = {
  youtube: { icon: <FaYoutube />, bg: '#ff0000' },
  discord: { icon: <FaDiscord />, bg: '#5865f2' },
  twitch: { icon: <FaTwitch />, bg: '#9146ff' },
  facebook: { icon: <FaFacebook />, bg: '#1877f2' },
  instagram: { icon: <FaInstagram />, bg: '#e1306c' },
  tiktok: { icon: <FaTiktok />, bg: '#010101' },
};

const DISCORD_INVITE_URL = 'https://discord.gg/NMBTSzDPy7';

const FILTER_KEYS = ['all', 'youtube', 'twitch', 'discord', 'facebook', 'tiktok'];

const SORT_KEYS = [
  { value: 'followers' },
  { value: 'videos' },
  { value: 'newest' },
];

const toNum = s => {
  if (!s) return 0;
  const n = parseFloat(s);
  return String(s).includes('K') ? n * 1000 : String(s).includes('M') ? n * 1000000 : n;
};

const ContentCreators = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: creatorsData, isLoading } = useSelector(state => state.contentCreators);
  const settings = useSelector(state => state.settings);
  const discordInvite = settings.data?.discord || DISCORD_INVITE_URL;
  const [search, setSearch] = useState('');
  const [activeFilt, setActiveFilt] = useState('all');
  const [sort, setSort] = useState('followers');

  useEffect(() => {
    dispatch(getContentCreators({ page: 1, size: 50, query: '', filter: JSON.stringify({ is_active: true }) }));
  }, [dispatch]);

  const allCreators = creatorsData?.data || [];

  const getPlatformLabel = (key) => {
    const map = {
      youtube: t('website.content_creators.youtube'),
      discord: t('website.content_creators.discord'),
      twitch: t('website.content_creators.twitch'),
      facebook: t('website.content_creators.facebook'),
      instagram: t('website.content_creators.instagram'),
      tiktok: t('website.content_creators.tiktok'),
    };
    return map[key] || key;
  };

  const getSortLabel = (value) => {
    const map = {
      followers: t('website.content_creators.sort_most_followers'),
      videos: t('website.content_creators.sort_most_videos'),
      newest: t('website.content_creators.sort_newest'),
    };
    return map[value] || value;
  };

  const processed = useMemo(() => {
    let list = allCreators;
    if (activeFilt !== 'all') {
      list = list.filter(c => c.platforms?.some(p => p.type === activeFilt));
    }
    if (search.trim()) list = list.filter(c => c.name.includes(search.trim()) || c.tag?.includes(search.trim()));
    return [...list].sort((a, b) => {
      if (sort === 'videos') return b.videos_count - a.videos_count;
      if (sort === 'newest') return b.joined_year - a.joined_year;
      return toNum(b.followers) - toNum(a.followers);
    });
  }, [search, activeFilt, sort, allCreators]);

  const getAvatar = (creator) => creator.avatar ? `${backendUrl}/creators/${creator.avatar}` : logo;
  const getBanner = (creator) => creator.banner ? `${backendUrl}/creators/${creator.banner}` : null;

  return (
    <WebsiteLayout>
      <PageWrapper>
        <PageBanner>
          <div>
            <div className="page-title"><FiActivity /> {t('website.content_creators.title')}</div>
            <div className="page-sub">{t('website.content_creators.subtitle')}</div>
            <div className="verify-hint">
              <FiInfo /> {t('website.content_creators.verify_channel_hint')}
            </div>
          </div>
          <div className="banner-actions">
            <a href={discordInvite} target="_blank" rel="noreferrer" className="verify-btn">
              <FaDiscord /> {t('website.content_creators.verify_channel_btn', 'توثيق القناة')}
            </a>
            <Link to="/add-video" className="add-video-btn">
              <FiPlus /> {t('website.content_creators.add_video_btn', 'أضف قناتك')}
            </Link>
          </div>
        </PageBanner>

        <ControlsBar>
          <div className="search-wrap">
            <FiSearch />
            <input placeholder={t('website.content_creators.search_placeholder')} value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="filter-pills">
            {FILTER_KEYS.map(key => (
              <FilterBtn key={key} active={activeFilt === key} onClick={() => setActiveFilt(key)}>
                {key !== 'all' && PLT_META[key]?.icon}
                {key === 'all' ? t('website.content_creators.filter_all') : getPlatformLabel(key)}
              </FilterBtn>
            ))}
          </div>
          <select className="sort-select" value={sort} onChange={e => setSort(e.target.value)}>
            {SORT_KEYS.map(o => <option key={o.value} value={o.value}>{getSortLabel(o.value)}</option>)}
          </select>
        </ControlsBar>

        {isLoading ? (
          <CreatorsGrid>
            {Array.from({ length: 8 }, (_, i) => <SkeletonCreatorCard key={i} />)}
          </CreatorsGrid>
        ) : processed.length === 0 ? (
          <NoData />
        ) : (
          <CreatorsGrid>
          {processed.map(creator => {
            const primary = PLT_META[creator.primary_platform] || PLT_META.youtube;
            const banner = getBanner(creator);
            return (
              <CreatorCard key={creator._id}>
                <div className="cover-strip"
                  style={banner
                    ? { backgroundImage: `url(${banner})` }
                    : { background: `linear-gradient(135deg, ${primary.bg}28, ${primary.bg}50)` }}>
                  <div className="avatar-wrap"
                    style={{ background: `linear-gradient(135deg, ${primary.bg}, ${primary.bg}88)` }}>
                    <img src={getAvatar(creator)} alt={creator.name} />
                  </div>
                  <div className="badges-corner">
                    <span className="primary-badge" style={{ background: primary.bg }}>{primary.icon}</span>
                    
                  </div>
                </div>
                <div className="card-body">
                  <div className="creator-name">{creator.name}</div>
                  <div className="creator-tag">{creator.tag}</div>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{creator.followers}</span>
                      <span className="stat-label">{t('website.content_creators.stat_followers')}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{creator.videos_count}</span>
                      <span className="stat-label">{t('website.content_creators.stat_videos')}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{creator.joined_year}</span>
                      <span className="stat-label">{t('website.content_creators.stat_joined')}</span>
                    </div>
                  </div>
                  <div className="platform-links">
                    {creator.platforms?.map(p => {
                      const meta = PLT_META[p.type];
                      if (!meta) return null;
                      return (
                        <a key={p.type} href={p.url || '#'} target="_blank" rel="noreferrer"
                          className="plt-btn" style={{ background: meta.bg }}
                          onClick={e => e.stopPropagation()}
                          title={`${getPlatformLabel(p.type)} · ${p.subs}`}>
                          {meta.icon} {p.subs}
                        </a>
                      );
                    })}
                    {creator.is_verified && (
                      <span className="verified-check" title={t('website.content_creators.verified_label')}>
                        <BsPatchCheckFill />
                      </span>
                    )}
                  </div>
                </div>
              </CreatorCard>
            );
          })}
          </CreatorsGrid>
        )}
      </PageWrapper>
    </WebsiteLayout>
  );
};

export default ContentCreators;
